// src/utils/auditLogger.js
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../../firebase/firebase.jsx";

export const AUDIT_COLLECTION = "auditLogs";

/**
 * Log an audit event to Firestore.
 *
 * Supports:
 *  1) logAuditEvent(actorId, action, target?, extra?)
 *  2) logAuditEvent(type, { action?, actorId?, actorEmail?, target?, details?, meta?, requestId?, source?, path? })
 *
 * @returns {Promise<string|null>} New audit doc ID (or null on failure)
 */
export async function logAuditEvent(a, b, c = "", d = {}) {
  try {
    const auth = getAuth();
    const user = auth.currentUser || null;
    const uid = user?.uid || "";
    const email = user?.email || "";

    const now = serverTimestamp();
    let evt = {};

    // Structured: logAuditEvent("type", payloadObject)
    if (typeof a === "string" && typeof b === "object" && b !== null) {
      const type = (a || "").trim();
      const {
        action = "",
        actorId = uid,
        actorEmail = email,
        target = "",
        details = {},
        meta = {},
        requestId = "",
        source = "",
        path = "",
      } = b || {};

      evt = {
        type: safeStr(type),
        action: safeStr(action),
        actorId: safeStr(actorId || uid),
        actorEmail: safeStr(actorEmail || email),
        target: safeStr(target),
        details: sanitize(details),
        meta: sanitize(meta),
        requestId: safeStr(requestId),
        source: safeStr(source || inferSource()),
        path: safeStr(path),

        // identity snapshot (useful for debugging; rules rely on request.auth)
        uid,
        email,
        timestamp: now,
      };
    }
    // Legacy: logAuditEvent(actorId, action, target?, extra?)
    else if (typeof a === "string" && typeof b === "string") {
      const actorId = (a || "").trim() || uid;
      const action = (b || "").trim();
      const target = typeof c === "string" ? (c || "").trim() : "";
      const extra = d && typeof d === "object" ? d : {};

      evt = {
        type: inferTypeFromAction(action),
        action: safeStr(action),
        actorId: safeStr(actorId || uid),
        actorEmail: safeStr(extra.actorEmail || email),
        target: safeStr(target || extra.target || ""),
        details: sanitize(extra.details || extra),
        meta: sanitize(extra.meta || {}),
        requestId: safeStr(extra.requestId || ""),
        source: safeStr(extra.source || inferSource()),
        path: safeStr(extra.path || ""),

        uid,
        email,
        timestamp: now,
      };
    } else {
      console.warn("[auditLogger] Invalid arguments to logAuditEvent:", a, b, c, d);
      return null;
    }

    // Optional: silently skip if not signed in (rules will block anyway)
    if (!uid && import.meta?.env?.DEV) {
      console.warn("[auditLogger] no user, attempting audit anyway (may be blocked by rules)");
    }

    if (!evt.action && !evt.type) {
      console.warn("[auditLogger] Missing 'action' or 'type' in audit event:", evt);
    }

    const ref = await addDoc(collection(db, AUDIT_COLLECTION), evt);

    if (import.meta?.env?.DEV) {
      console.log(`[Audit] ${evt.type || evt.action} → ${evt.target || "(no target)"} (${ref.id})`);
    }
    return ref.id;
  } catch (err) {
    console.error("[auditLogger] Audit log failed:", err);
    return null;
  }
}

/** Convenience helper for role changes. */
export async function logRoleChange({
  byId = "",
  byEmail = "",
  targetUserId = "",
  action = "", // "granted" | "revoked"
  role = "",
  newRoles = [],
  requestId = "",
} = {}) {
  return logAuditEvent("role_change", {
    action,
    actorId: byId,
    actorEmail: byEmail,
    target: targetUserId,
    details: { role, newRoles: Array.isArray(newRoles) ? newRoles : [] },
    requestId,
  });
}

/* ----------------- utils ----------------- */
function safeStr(v) {
  return typeof v === "string" ? v : "";
}

function sanitize(value, depth = 0) {
  if (value == null || typeof value === "number" || typeof value === "boolean") return value;
  if (typeof value === "string") return truncate(value, 20000);

  if (Array.isArray(value)) {
    if (depth > 5) return ["[[truncated]]"];
    return value.map((v) => sanitize(v, depth + 1));
  }

  if (typeof value === "object") {
    if (depth > 5) return { truncated: true };
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (typeof v === "undefined") continue;
      out[k] = sanitize(v, depth + 1);
    }
    return out;
  }

  return truncate(String(value), 20000);
}

function truncate(str, max) {
  return str.length <= max ? str : str.slice(0, max) + `…[${str.length - max} more chars]`;
}

function inferTypeFromAction(action = "") {
  const a = String(action || "").toLowerCase();
  if (a.includes("role")) return "role_change";
  if (["verify", "unverify", "pause", "resume", "delete", "update"].includes(a)) return "business_update";
  return a || "event";
}

function inferSource() {
  const env = import.meta?.env?.MODE || "";
  return env ? `web:${env}` : "web";
}
